/**
 * Gallery #panel-collapse - resizable shell that snaps to the 48px rail.
 * Accepted 2026-11-01. The shell kit is extracted from this scene.
 */
import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react'
import { ChevronDown, FileText, Folder, Inbox, RefreshCw, Settings, SlidersHorizontal } from 'lucide-react'
import {
  ShellColumnHeader,
  ShellFlyout,
  ShellIconRail,
  ShellIconRailButton,
  ShellIconRailCap,
  ShellIndexRow,
  ShellPanelToggle,
  TitleRowTrailing,
  useShellFlyoutEnterDelay,
  useShellFlyoutHold,
  useShellFlyoutPresence,
} from '@/components/layout/shell'
import { WorkspacePipGlyph } from '@/components/layout/workspace-pip-glyph'
import { ProtoTabRow } from '@/components/prototype/maater-proto-chrome'
import { AddressBar } from '@/components/ui/address-bar'
import { Button } from '@/components/ui/button'
import { IconButton } from '@/components/ui/icon-button'
import { NavigationButtons } from '@/components/ui/navigation-buttons'
import { useShellPanelEnterClass, useShellPanelExitClass } from '@/hooks/use-shell-panel-motion'
import { useDetectedShortcutOs } from '@/hooks/use-shortcut-os'
import { formatModShortcut, isModKey } from '@/lib/keyboard-platform'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import { testIds } from '@/lib/test-ids'
import { SHELL_FLYOUT_ENTER_HOLD_MS, SHELL_FLYOUT_LEAVE_HOLD_MS } from '@/lib/shell-panel-motion'
import { SHELL_COLUMN_HEADER_CLASS, SHELL_ICON_RAIL_CLASS } from '@/lib/shell-chrome'
import {
  AGENT_DEFAULT,
  AGENT_FLOOR,
  CANVAS_MIN,
  FRAME_DEFAULT,
  FRAME_MAX,
  FRAME_MIN,
  LEFT_DEFAULT,
  LEFT_FLOOR,
  MODES,
  PREVIEW_DEFAULT,
  PREVIEW_FLOOR,
  MENU_CONTEXTS,
  RAIL,
  SCOPE_OVERFLOW,
  SCOPE_VISIBLE_IDS,
  SCOPE_WORKSPACES,
  TAB_MENU,
  canDock,
  columnWidth,
  fitLayout,
  previewWidth,
  type Layout,
  type PanelCollapseMode,
} from './maater-ui-panel-collapse-layout'
import {
  PanelCollapseDocumentBody,
  PanelCollapseDocumentDock,
  PanelCollapseDocumentToolbar,
} from './maater-ui-panel-collapse-document'
import { useGalleryEntityMenu } from './maater-ui-gallery-entity-menu'
import { PanelCollapsePeek } from './maater-ui-panel-collapse-peek'
import { ActiveSidebar } from './maater-ui-panel-collapse-sidebar'

export function PanelCollapseScene() {
  const [mode, setMode] = useState<PanelCollapseMode>('browse')
  const [centerTab, setCenterTab] = useState('documents')
  const [layout, setLayout] = useState<Layout>(() =>
    fitLayout({
      frameWidth: FRAME_DEFAULT,
      leftDocked: true,
      leftPx: LEFT_DEFAULT,
      agentDocked: true,
      agentPx: AGENT_DEFAULT,
      previewDocked: true,
      previewPx: PREVIEW_DEFAULT,
      autoHide: false,
      pinned: false,
    }),
  )
  const [flyout, setFlyout] = useState(false)
  const [dense, setDense] = useState(false)
  const [openTabsVisible, setOpenTabsVisible] = useState(true)
  const [leftEnterKey, setLeftEnterKey] = useState(0)
  const [agentEnterKey, setAgentEnterKey] = useState(0)
  const [previewEnterKey, setPreviewEnterKey] = useState(0)
  const layoutRef = useRef(layout)
  layoutRef.current = layout
  const shortcutOs = useDetectedShortcutOs()
  const sidebarShortcut = formatModShortcut(shortcutOs, 'b')
  const leftEnterClass = useShellPanelEnterClass('left')
  const leftExitClass = useShellPanelExitClass('left')
  const agentEnterClass = useShellPanelEnterClass('right')
  const previewEnterClass = useShellPanelEnterClass('right')

  const showFlyout = layout.autoHide && !layout.leftDocked && flyout
  const flyoutPresence = useShellFlyoutPresence(showFlyout, leftExitClass)
  const flyoutClosing = flyoutPresence.closing
  const flyoutHold = useShellFlyoutHold(() => setFlyout(false))
  const leftColumnPx = columnWidth(layout.leftDocked, layout.leftPx)
  const agentColumnPx = columnWidth(layout.agentDocked, layout.agentPx)
  const previewColumnPx = previewWidth(layout.previewDocked, layout.previewPx)
  const roomForLeft = canDock(layout.frameWidth, agentColumnPx + previewColumnPx, LEFT_FLOOR)
  const roomForAgent = canDock(layout.frameWidth, leftColumnPx + previewColumnPx, AGENT_FLOOR)
  const roomForPreview = canDock(layout.frameWidth, leftColumnPx + agentColumnPx, PREVIEW_FLOOR)

  const isDocTab = centerTab === 'doc'
  const entityMenu = useGalleryEntityMenu()
  const paneBlockedReason =
    !layout.previewDocked && !roomForPreview
      ? `Widen the window to show the ${isDocTab ? 'details' : 'preview'}`
      : undefined

  const playLeftEnter = () => setLeftEnterKey((key) => key + 1)
  const playAgentEnter = () => setAgentEnterKey((key) => key + 1)
  const playPreviewEnter = () => setPreviewEnterKey((key) => key + 1)

  const applyLayout = (next: Layout, motion?: 'left' | 'agent' | 'preview') => {
    const prev = layoutRef.current
    const fitted = fitLayout(next)
    layoutRef.current = fitted
    setLayout(fitted)
    if (motion === 'left' && fitted.leftDocked && !prev.leftDocked) playLeftEnter()
    if (motion === 'agent' && fitted.agentDocked && !prev.agentDocked) playAgentEnter()
    if (motion === 'preview' && fitted.previewDocked && !prev.previewDocked) playPreviewEnter()
    if (fitted.leftDocked || prev.leftDocked) setFlyout(false)
  }

  const openFlyout = () => {
    const current = layoutRef.current
    if (!current.autoHide || current.leftDocked) return
    setFlyout((open) => {
      if (!open) playLeftEnter()
      return true
    })
  }
  const flyoutEnter = useShellFlyoutEnterDelay(() => {
    flyoutHold.enter()
    openFlyout()
  })

  const toggleLeftDock = () => {
    const current = layoutRef.current
    if (current.leftDocked) {
      applyLayout({ ...current, leftDocked: false, pinned: false })
      setFlyout(false)
      return
    }
    if (!roomForLeft) {
      openFlyout()
      return
    }
    applyLayout(
      { ...current, leftDocked: true, pinned: current.autoHide, leftPx: Math.max(current.leftPx, LEFT_FLOOR) },
      'left',
    )
    setFlyout(false)
  }

  const toggleAgentDock = () => {
    const current = layoutRef.current
    if (current.agentDocked) {
      applyLayout({ ...current, agentDocked: false })
      return
    }
    if (!roomForAgent) return
    applyLayout(
      { ...current, agentDocked: true, agentPx: Math.max(current.agentPx, AGENT_FLOOR) },
      'agent',
    )
  }

  const togglePreview = () => {
    const current = layoutRef.current
    if (current.previewDocked) {
      applyLayout({ ...current, previewDocked: false })
      return
    }
    if (!roomForPreview) return
    applyLayout(
      { ...current, previewDocked: true, previewPx: Math.max(current.previewPx, PREVIEW_FLOOR) },
      'preview',
    )
  }

  const toggleAutoHide = () => {
    const current = layoutRef.current
    const autoHide = !current.autoHide
    applyLayout({
      ...current,
      autoHide,
      pinned: false,
      leftDocked: autoHide ? false : true,
      leftPx: Math.max(current.leftPx, LEFT_DEFAULT),
    }, autoHide ? undefined : 'left')
    setFlyout(false)
  }

  const collapseSidebar = () => {
    if (layoutRef.current.leftDocked) {
      toggleLeftDock()
      return
    }
    setFlyout(false)
  }

  const onMenuOpenChange = (open: boolean) => flyoutHold.menuOpenChange(open)

  const onRailEnter = () => {
    flyoutHold.enter()
    if (flyout) {
      flyoutEnter.cancel()
      return
    }
    flyoutEnter.arm()
  }

  const onRailLeave = () => {
    flyoutEnter.cancel()
    flyoutHold.leave()
  }

  const toggleLeftRef = useRef(toggleLeftDock)
  const toggleAgentRef = useRef(toggleAgentDock)
  toggleLeftRef.current = toggleLeftDock
  toggleAgentRef.current = toggleAgentDock

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (!isModKey(event) || event.altKey || event.shiftKey) return
      const key = event.key.toLowerCase()
      if (key !== 'b' && key !== 'j') return
      const target = event.target
      if (
        target instanceof HTMLElement &&
        target.closest('input, textarea, [contenteditable="true"]')
      ) {
        return
      }
      event.preventDefault()
      event.stopPropagation()
      if (key === 'b') toggleLeftRef.current()
      else toggleAgentRef.current()
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [])

  const sidebarProps = {
    mode,
    enterClass: leftEnterClass,
    enterKey: leftEnterKey,
    autoHide: layout.autoHide,
    dense,
    openTabsVisible,
    shortcut: sidebarShortcut,
    onAutoHideToggle: toggleAutoHide,
    onDenseChange: setDense,
    onOpenTabsVisibleChange: setOpenTabsVisible,
    onCollapse: collapseSidebar,
    onMenuOpenChange,
  }

  const onFrameWidth = (frameWidth: number) => {
    applyLayout({ ...layoutRef.current, frameWidth })
  }

  const onLeftPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    event.preventDefault()
    const startX = event.clientX
    const startPx = layoutRef.current.leftDocked ? layoutRef.current.leftPx : RAIL
    const move = (ev: PointerEvent) => {
      const current = layoutRef.current
      const next = startPx + (ev.clientX - startX)
      const other =
        columnWidth(current.agentDocked, current.agentPx) +
        previewWidth(current.previewDocked, current.previewPx)
      const maxLeft = current.frameWidth - other - CANVAS_MIN
      if (next < LEFT_FLOOR || maxLeft < LEFT_FLOOR) {
        applyLayout({ ...current, leftDocked: false, pinned: false })
        return
      }
      applyLayout(
        {
          ...current,
          leftDocked: true,
          pinned: current.autoHide ? true : current.pinned,
          leftPx: Math.min(Math.round(next), maxLeft),
        },
        'left',
      )
    }
    const up = () => {
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', up)
    }
    window.addEventListener('pointermove', move)
    window.addEventListener('pointerup', up)
  }

  const onAgentPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    event.preventDefault()
    const startX = event.clientX
    const startPx = layoutRef.current.agentDocked ? layoutRef.current.agentPx : RAIL
    const move = (ev: PointerEvent) => {
      const current = layoutRef.current
      const next = startPx + (startX - ev.clientX)
      const other =
        columnWidth(current.leftDocked, current.leftPx) +
        previewWidth(current.previewDocked, current.previewPx)
      const maxAgent = current.frameWidth - other - CANVAS_MIN
      if (next < AGENT_FLOOR || maxAgent < AGENT_FLOOR) {
        applyLayout({ ...current, agentDocked: false })
        return
      }
      applyLayout(
        {
          ...current,
          agentDocked: true,
          agentPx: Math.min(Math.round(next), maxAgent),
        },
        'agent',
      )
    }
    const up = () => {
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', up)
    }
    window.addEventListener('pointermove', move)
    window.addEventListener('pointerup', up)
  }

  const onPreviewPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    event.preventDefault()
    const startX = event.clientX
    const startPx = layoutRef.current.previewDocked ? layoutRef.current.previewPx : 0
    const move = (ev: PointerEvent) => {
      const current = layoutRef.current
      const next = startPx + (startX - ev.clientX)
      const other =
        columnWidth(current.leftDocked, current.leftPx) +
        columnWidth(current.agentDocked, current.agentPx)
      const maxPreview = current.frameWidth - other - CANVAS_MIN
      if (next < PREVIEW_FLOOR || maxPreview < PREVIEW_FLOOR) {
        applyLayout({ ...current, previewDocked: false })
        return
      }
      applyLayout(
        {
          ...current,
          previewDocked: true,
          previewPx: Math.min(Math.round(next), maxPreview),
        },
        'preview',
      )
    }
    const up = () => {
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', up)
    }
    window.addEventListener('pointermove', move)
    window.addEventListener('pointerup', up)
  }

  const previewHandle = (edge: 'left' | 'right') => (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize preview"
      data-testid={testIds.maaterUiGallery.panelCollapsePreviewHandle}
      className={cn(
        'absolute inset-y-0 z-30 w-2 cursor-col-resize',
        edge === 'left' ? '-left-1' : '-right-1',
      )}
      onPointerDown={onPreviewPointerDown}
    >
      <span className="absolute inset-y-0 left-1/2 w-px -translate-x-1/2 bg-border" />
    </div>
  )

  const rail = (
    <ShellIconRail
      style={{ borderColor: 'var(--line)', background: 'var(--paper-raised)' }}
      toggle={
        <ShellPanelToggle
          side="left"
          placement="slot"
          open={layout.leftDocked}
          disabledReason={
            !layout.leftDocked && !roomForLeft && !layout.autoHide
              ? 'Frame is too narrow to dock'
              : undefined
          }
          onToggle={toggleLeftDock}
        />
      }
      scope={
        <ShellIconRailCap
          line="header"
          label="Workspace scope"
          testId={testIds.maaterUiGallery.panelCollapseScope}
          className="group/pips flex-col overflow-hidden"
          onClick={() => toast.message('Workspace scope picker (see live icon bar)')}
        >
          <WorkspacePipGlyph
            workspaces={SCOPE_WORKSPACES}
            visibleIds={SCOPE_VISIBLE_IDS}
            overflow={SCOPE_OVERFLOW}
            interactive
          />
        </ShellIconRailCap>
      }
      footer={
        <ShellIconRailCap line="footer" label="Settings" className="border-[color:var(--line)]">
          <Settings className="h-4 w-4" />
        </ShellIconRailCap>
      }
    >
      {MODES.map((item) => {
        const Icon = item.icon
        return (
          <ShellIconRailButton
            key={item.id}
            icon={<Icon className="h-4 w-4" />}
            label={item.label}
            tone={item.id === mode ? 'active' : 'idle'}
            onClick={() => setMode(item.id)}
          />
        )
      })}
    </ShellIconRail>
  )

  return (
    <div data-testid={testIds.maaterUiGallery.panelCollapseScene} className="space-y-4">
      <p className="text-sm" style={{ color: 'var(--ink-soft)' }}>
        Drag a column, or narrow the frame. Under {LEFT_FLOOR}px the left list snaps to the {RAIL}px
        rail. Under {AGENT_FLOOR}px the agent snaps to the same strip. Under {PREVIEW_FLOOR}px the
        inbox preview closes. The header has no panel toggles. The rail top toggles the sidebar.
        The agent toggle is the Bot on that column. Auto-hide is in the sidebar menu. Width changes
        are instant. A flyout, a newly docked column, and the preview use the panel-enter motion
        from <code>#panel-motion</code>.
      </p>

      <label className="flex max-w-md flex-col gap-1 text-xs" style={{ color: 'var(--ink-soft)' }}>
        <span>
          Frame width <span style={{ color: 'var(--ink)' }}>{layout.frameWidth}px</span>
          {' · '}
          left {layout.leftDocked ? `${layout.leftPx}px` : `rail ${RAIL}px`}
          {' · '}
          preview {layout.previewDocked ? `${layout.previewPx}px` : 'closed'}
          {' · '}
          agent {layout.agentDocked ? `${layout.agentPx}px` : `strip ${RAIL}px`}
        </span>
        <input
          type="range"
          min={FRAME_MIN}
          max={FRAME_MAX}
          value={layout.frameWidth}
          data-testid={testIds.maaterUiGallery.panelCollapseFrameWidth}
          onChange={(event) => onFrameWidth(Number(event.target.value))}
        />
      </label>

      <div
        className="overflow-hidden rounded-lg border"
        data-testid={testIds.maaterUiGallery.panelCollapseFrame}
        style={{ width: layout.frameWidth, borderColor: 'var(--line)', background: 'var(--paper)' }}
      >
        <div
          className={cn(SHELL_COLUMN_HEADER_CLASS, 'flex items-center border-b px-3')}
          style={{ borderColor: 'var(--line)', background: 'var(--paper-raised)' }}
        >
          <span className="text-xs font-medium" style={{ color: 'var(--ink)' }}>
            Maater
          </span>
        </div>

        <div className="relative flex h-[440px] min-w-0">
          <div
            className={cn(
              'relative flex h-full shrink-0',
              (showFlyout || flyoutClosing) && 'z-20',
            )}
            style={{ width: leftColumnPx }}
            onMouseEnter={onRailEnter}
            onMouseLeave={onRailLeave}
          >
            <div
              role="separator"
              aria-orientation="vertical"
              aria-label="Resize sidebar"
              data-testid={testIds.maaterUiGallery.panelCollapseLeftHandle}
              className="absolute inset-y-0 -right-1 z-30 w-2 cursor-col-resize"
              onPointerDown={onLeftPointerDown}
            >
              <span className="absolute inset-y-0 left-1/2 w-px -translate-x-1/2 bg-border" />
            </div>
            {rail}
            {layout.leftDocked && (
              <div
                className="flex min-w-0 flex-1 flex-col border-r"
                style={{ borderColor: 'var(--line)', background: 'var(--paper-deep)' }}
              >
                <ActiveSidebar {...sidebarProps} />
              </div>
            )}
            <ShellFlyout
              presence={flyoutPresence}
              exitClass={leftExitClass}
              left={RAIL}
              width={LEFT_FLOOR - RAIL}
              testId={testIds.maaterUiGallery.panelCollapseFlyout}
              style={{ borderColor: 'var(--line)', background: 'var(--paper-deep)' }}
            >
              <ActiveSidebar {...sidebarProps} />
            </ShellFlyout>
          </div>

          <div
            className="flex min-w-0 flex-1 flex-col"
            data-testid={testIds.maaterUiGallery.panelCollapseCanvas}
            onClick={() => flyoutHold.closeNow()}
          >
            <ProtoTabRow
              testId={testIds.documentViewer.tabList}
              activeId={centerTab}
              onSelect={setCenterTab}
              onTabContextMenu={(id, event) => {
                const context = TAB_MENU[id]
                if (context) entityMenu.openAtPointer(event, context)
              }}
              tabs={[
                { id: 'inbox', label: 'Inbox', icon: <Inbox className="h-3.5 w-3.5" /> },
                { id: 'documents', label: 'Documents', icon: <Folder className="h-3.5 w-3.5" /> },
                { id: 'doc', label: 'Late-todo shadow', icon: <FileText className="h-3.5 w-3.5" /> },
              ]}
            />
            {isDocTab ? (
              <PanelCollapseDocumentToolbar
                paneOpen={layout.previewDocked}
                onTogglePane={togglePreview}
                onMore={(event) => entityMenu.openFromButton(event, MENU_CONTEXTS.docTab)}
                disabledReason={paneBlockedReason}
              />
            ) : (
            <nav
              className="flex shrink-0 items-center justify-between gap-2 border-b border-border bg-background/50 px-4 py-2"
              aria-label="Folder actions"
              data-testid={testIds.maaterUiGallery.panelCollapseToolbar}
            >
              <div className="flex min-w-0 flex-1 items-center gap-2">
                <NavigationButtons canGoBack={false} canGoForward={false} />
                <div className="flex min-w-0 flex-1 flex-col gap-1">
                  <AddressBar segments={[{ label: 'Hop Studios', path: 'hop-studios' }]} />
                  <h1 className="flex min-w-0 items-center gap-2 text-lg font-semibold text-foreground">
                    <Folder className="h-4 w-4 shrink-0 text-blue-500" aria-hidden />
                    <span className="truncate">Documents</span>
                  </h1>
                </div>
              </div>
              <TitleRowTrailing
                actions={[
                  <IconButton
                    key="reload"
                    icon={<RefreshCw className="h-4 w-4" />}
                    label="Reload folder"
                    variant="ghost"
                    size="sm"
                  />,
                ]}
                pane={{
                  open: layout.previewDocked,
                  onToggle: togglePreview,
                  disabledReason: paneBlockedReason,
                  testId: testIds.maaterUiGallery.panelCollapsePreviewToggle,
                }}
                onMore={(event) => entityMenu.openFromButton(event, MENU_CONTEXTS.folder)}
                moreTestId={testIds.maaterUiGallery.panelCollapseIndexMore}
              />
            </nav>
            )}
            <div className="flex min-h-0 flex-1">
              {isDocTab ? (
                <PanelCollapseDocumentBody />
              ) : (
              <div className="flex min-w-0 flex-1 flex-col">
                <ShellIndexRow className="bg-background" aria-label="Document filters">
                  <Button variant="outline" size="sm" className="gap-1" aria-label="More filters">
                    <SlidersHorizontal className="h-3 w-3" />
                    Filters
                    <ChevronDown className="h-3 w-3" aria-hidden />
                  </Button>
                </ShellIndexRow>
                <div className="min-h-0 flex-1 space-y-0.5 overflow-hidden px-2 py-2">
                  <div
                    className="rounded-md bg-primary/15 px-2 py-1.5 text-xs font-medium text-primary"
                    onContextMenu={(event) => entityMenu.openAtPointer(event, MENU_CONTEXTS.listRow)}
                  >
                    Late-todo shadow.md
                  </div>
                  <div className="px-2 py-1.5 text-xs" style={{ color: 'var(--ink-soft)' }}>
                    Weekly Priorities.md
                  </div>
                  <p className="px-2 pt-2 text-[11px] leading-snug" style={{ color: 'var(--ink-soft)' }}>
                    {layout.autoHide && !layout.leftDocked
                      ? 'Auto-hide is on. Hover the rail to fly the active sidebar over this list.'
                      : 'The preview sits in this center column, beside the list. The agent is the full-height column.'}
                  </p>
                </div>
              </div>
              )}
              {layout.previewDocked && (
                <div
                  data-testid={testIds.maaterUiGallery.panelCollapsePreview}
                  className="relative flex h-full shrink-0 flex-col border-l bg-card"
                  style={{ width: layout.previewPx, borderColor: 'var(--line)' }}
                >
                  {previewHandle('left')}
                  <div
                    key={previewEnterKey}
                    className={cn(
                      'flex min-h-0 flex-1 flex-col',
                      previewEnterKey > 0 && previewEnterClass,
                    )}
                  >
                    {isDocTab ? (
                      <PanelCollapseDocumentDock onClose={togglePreview} />
                    ) : (
                      <PanelCollapsePeek onClose={togglePreview} onOpen={() => setCenterTab('doc')} />
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {layout.agentDocked ? (
            <div
              className="relative flex h-full shrink-0 flex-col border-l"
              style={{
                width: layout.agentPx,
                borderColor: 'var(--line)',
                background: 'var(--paper-deep)',
              }}
            >
              <div
                role="separator"
                aria-orientation="vertical"
                aria-label="Resize agent"
                data-testid={testIds.maaterUiGallery.panelCollapseAgentHandle}
                className="absolute inset-y-0 -left-1 z-30 w-2 cursor-col-resize"
                onPointerDown={onAgentPointerDown}
              >
                <span className="absolute inset-y-0 left-1/2 w-px -translate-x-1/2 bg-border" />
              </div>
              <div
                key={agentEnterKey}
                className={cn('flex min-h-0 flex-1 flex-col', agentEnterClass)}
              >
                <ShellColumnHeader
                  title="Agent"
                  optionsMenu={
                    <ShellPanelToggle side="agent" placement="inline" open onToggle={toggleAgentDock} />
                  }
                />
                <p className="flex-1 p-3 text-xs leading-relaxed" style={{ color: 'var(--ink-soft)' }}>
                  Chat stays readable at the {AGENT_FLOOR}px floor. Narrower than that, this column
                  is the strip.
                </p>
                <div
                  className="m-3 rounded-md border px-3 py-2 text-xs"
                  style={{ borderColor: 'var(--line)', color: 'var(--ink-faint)' }}
                >
                  Message the agent
                </div>
              </div>
            </div>
          ) : (
            <div
              className={cn(
                SHELL_ICON_RAIL_CLASS,
                'relative flex h-full shrink-0 flex-col items-center border-l',
              )}
              style={{ borderColor: 'var(--line)', background: 'var(--paper-raised)' }}
            >
              <div
                role="separator"
                aria-orientation="vertical"
                aria-label="Resize agent"
                data-testid={testIds.maaterUiGallery.panelCollapseAgentHandle}
                className="absolute inset-y-0 -left-1 z-30 w-2 cursor-col-resize"
                onPointerDown={onAgentPointerDown}
              >
                <span className="absolute inset-y-0 left-1/2 w-px -translate-x-1/2 bg-border" />
              </div>
              <ShellPanelToggle
                side="agent"
                placement="slot"
                open={false}
                disabledReason={roomForAgent ? undefined : 'Frame is too narrow to dock'}
                onToggle={toggleAgentDock}
              />
              <div className="flex-1" />
            </div>
          )}
        </div>
      </div>
      {entityMenu.node}

      <div className="grid gap-3 text-xs md:grid-cols-2" style={{ color: 'var(--ink-soft)' }}>
        <div>
          <div className="mb-1 font-semibold uppercase tracking-wide" style={{ color: 'var(--ink)' }}>
            Gestures
          </div>
          <ul className="list-disc space-y-1 pl-4">
            <li>Drag a handle past the floor and that side snaps to the {RAIL}px rail</li>
            <li>Drag back out to dock again, when the frame can spare the floor</li>
            <li>One toggle per column. The header does not repeat them. Cmd/Ctrl+B and +J do</li>
            <li>
              Auto-hide is in the sidebar ... menu, off by default. The flyout has the same menu.
              Hover arms the rail for {SHELL_FLYOUT_ENTER_HOLD_MS}ms, then flies the active sidebar over the canvas. A quicker pass does not open it. Leaving holds it {SHELL_FLYOUT_LEAVE_HOLD_MS}ms,
              then it fades back toward the rail. Coming back inside the hold keeps it. A canvas
              click closes it at once
            </li>
            <li>A rail click changes which sidebar that is. Hover does not</li>
            <li>The preview pin explains itself when the frame cannot spare {PREVIEW_FLOOR}px</li>
            <li>Preview opens with the same enter motion. Closing it is instant</li>
            <li>
              The preview header is the Browse document peek: kind icon, type chip, ..., open,
              close. No uppercase label. Open switches to the document tab
            </li>
            <li>
              Title row trailing cluster: surface actions, then PanelRight, then ... (Save |
              PanelRight | ... on a document; reload, PanelRight, ... on an index). PanelRight is the
              one right-pane toggle. It replaces Info on documents and Edit on Basecamp todos
            </li>
            <li>
              Every menu is the production <code>ContextMenu</code> (ADR-0020). Right-click a tab and
              the document <code>...</code> show the same tab items. The index <code>...</code> is the
              folder menu, the same one a folder row right-click shows
            </li>
          </ul>
        </div>
        <div>
          <div className="mb-1 font-semibold uppercase tracking-wide" style={{ color: 'var(--ink)' }}>
            Floors
          </div>
          <ul className="list-disc space-y-1 pl-4">
            <li>Left {LEFT_FLOOR}px ({RAIL}px rail + labels)</li>
            <li>Preview {PREVIEW_FLOOR}px, then it closes</li>
            <li>Agent {AGENT_FLOOR}px</li>
            <li>Accepted 2026-11-01. Production keeps its percentage minimum until the shell kit port</li>
          </ul>
        </div>
      </div>
    </div>
  )
}
